import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaHome, 
  FaTrophy, 
  FaStar, 
  FaInfoCircle,
  FaBars,
  FaTimes,
  FaBalanceScale,
  FaQuestionCircle,
  FaEnvelope
} from 'react-icons/fa';
import { useState, useMemo } from 'react';
import Footer from './Footer';
import { IconWrapper } from './IconWrapper';
import ResponsibleGaming from './ResponsibleGaming';
import OptimizedImage from './OptimizedImage';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = useMemo(() => [
    { name: 'Accueil', path: '/', icon: FaHome },
    { name: 'Comparaison', path: '/comparaison', icon: FaBalanceScale },
    { name: 'Winamax', path: '/winamax', icon: FaTrophy },
    { name: 'Olybet', path: '/olybet', icon: FaStar },
    { name: 'Genybet', path: '/genybet', icon: FaTrophy },
    { name: 'FAQ', path: '/faq', icon: FaQuestionCircle },
    { name: 'Contact', path: '/contact', icon: FaEnvelope },
    { name: 'À propos', path: '/a-propos', icon: FaInfoCircle },
  ], []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Responsible Gaming Warning */}
      <ResponsibleGaming />
      
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden bg-primary-600 text-white p-2 rounded-lg shadow-lg"
        aria-label="Toggle menu"
      >
        {sidebarOpen ? <IconWrapper icon={FaTimes} size={20} /> : <IconWrapper icon={FaBars} size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-primary-800 to-primary-900 text-white
          transform transition-transform duration-300 ease-in-out z-40
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          shadow-2xl
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-primary-700">
            <Link to="/" className="flex items-center space-x-3" onClick={() => setSidebarOpen(false)}>
              <OptimizedImage
                src="/images/logo.webp"
                alt="LiveSportifs"
                className="h-12 w-auto"
                width={57}
                height={48}
                priority={true}
                sizes="57px"
              />
              <div>
                <h1 className="text-xl font-bold">LiveSportifs</h1>
                <p className="text-xs text-primary-300">Comparateur de paris</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive(item.path)
                      ? 'bg-primary-600 text-white shadow-lg transform scale-105'
                      : 'text-primary-100 hover:bg-primary-700 hover:text-white'
                    }
                  `}
                >
                  <IconWrapper icon={item.icon} size={20} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-primary-700 text-xs text-primary-300">
            <p>© {new Date().getFullYear()} LiveSportifs</p>
            <p className="mt-1">Jouez responsablement</p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen flex flex-col">
        <div className="flex-1">
          {children}
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default Layout;

